import React, { useState,useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { List } from "immutable";
import { Input , Table, Button, Tag, Switch, Space, Typography, Tooltip, Modal, Col, Select, Row, Alert, theme, Flex } from 'antd';
import { createMigrationColumns, getMigrationTableColumn } from '../../common/MigrationTableColumn';
import { shallowEqual } from "react-redux";
import { ExclamationCircleOutlined, FontSizeOutlined, CopyOutlined, DeleteOutlined } from '@ant-design/icons';
import { DidomiFavIcon } from '../../../assets/icons/DidomiFavIcon.jsx';
import { message, Infotip } from '../../../styleguide';
import CloneModal from '../../common/CloneModal.jsx';
import DismissErrorModal from '../../common/DismissErrorModal';
import DidomiMigrationModal from './DidomiMigrationModal';
import { useDebouncedSearch } from "../../../hooks";
import { handleServerSideErrors, renderCannotUpdateRegulationError } from "./helper";
import Loading from "../../common/Loading.js";
import PropertiesDetailModal from "../../common/PropertiesDetailModal";
import dayjs from "dayjs";
import { hasFeature, convertToCSV, downloadCsv } from "../../utils.js";

function RegulationListGeneric(props) {
  
  //destructing props
  const {
    currentUser, 
    vendorLists ,
    vendorListError,
    sites ,
    siteGroups ,
    vendorListsPending,
    tableStates,
    setTableStates
  } = props;

  const [isPropertyFilterOpen, setIsPropertyFilterOpen] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [currentRegulation, setCurrentRegulation] = useState({})
  const [cloneVlModalOpen, setCloneVlModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [showActivateErrorModal, setShowActivateErrorModal] = useState(false)
  const [showPropertiesDetail, setShowPropertiesDetail] = useState(false)
  const [serverErrors, setServerErrors] = useState(List([]))
  const [selectedFilterValues, setSelectedFilterValues] = useState([])
  const [columnExpandCollapse, setColumnExpandCollapse] = useState({ migration: false });
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationRecord, setMigrationRecord] = useState(null);
  const [migrationInfo, setMigrationInfo] = useState(null);
  const [migrationError, setMigrationError] = useState(null);

  const indexSiteNameMap = useMemo(() => sites.toJS().reduce((map, obj) => (map[obj.id] = obj.domain, map), {}), [sites]);
  const sitesIds = useMemo(() => sites.map(s => s.id).toJS(), [sites]);
  const filterPropertyObjects = useMemo(()=>{
    const allRegulationListSiteIds = Array.from(new Set(vendorLists.toJS()?.map(acc => acc.siteIds)?.flat()));
    const allAccountLevelIds = allRegulationListSiteIds.filter(id => indexSiteNameMap[id]).map((id) => {
      return { text: indexSiteNameMap[id], value: id };
    });
    allAccountLevelIds.unshift({text: "(no property)", value: "no_property"});
    return allAccountLevelIds;
  },[vendorLists, sites])
  const vendorListDataSource = useMemo(() => vendorLists.toJS(), [vendorLists]);

  const readOnly = props.readOnly || !props.isAccountPartOfWave1;

  const { token } = theme.useToken();

  const handleCopy = (e,record) => {
    e.stopPropagation()
    setCurrentRegulation(record)
    setCloneVlModalOpen(true)
  }

  const showDeleteModal = (e,record) => {
    e.stopPropagation()
    setCurrentRegulation(record)
    setDeleteModalOpen(true);
  }

  const toggleDeactivateMessage = (record) => {
    setCurrentRegulation(record)
    setShowDeactivateModal(!showDeactivateModal);
  }

  //debouncing search for performance 
  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
    setIsSearchLoading(false);
  }, []);
  const debouncedChangeHandler = useDebouncedSearch(handleSearchChange, 300);

  const toggleColumn = useCallback((groupKey) => {
    setColumnExpandCollapse((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  const handleMigrationClick = useCallback((record) => {
    setMigrationRecord(record);
    setMigrationInfo(null);
    setMigrationError(null);
    setShowMigrationModal(true);
  }, []);


  const handleMigrationStatusChange = async (record, newStatus) => {
     props.updateDidomiMigrationStatus(record.id, newStatus).then((resp) => {
      if(resp?.id) message.success('Migration status updated successfully');
    });
  }

  const userAccess = (currentUser.featureAccess && currentUser.featureAccess.includes("Didomi Migration") || currentUser.allFeatureAccess);
  const showDownloadActive = props.gdpr || props.usnat || props.globalStandard;
  // Whether the Didomi migration columns (Last Migrated / Migration Status) and the
  // in-row "Migrate to Didomi" action should surface for this account/user.
  const showMigrationColumns = (props.gdpr || props.usnat || props.globalStandard) && hasFeature(currentUser, 'didomi_migration') && currentUser?.migrationTester;
  const showMigrateAction = showMigrationColumns;
  const migrationTooltipKey = props.gdpr ? 'gdpr-vl' : 'regulation-vl';
  const migrationColumn = useMemo(
    () => createMigrationColumns(handleMigrationClick, handleMigrationStatusChange, userAccess, migrationTooltipKey),
    [handleMigrationClick, handleMigrationStatusChange, userAccess, migrationTooltipKey]
  );

  const handleConfirmMigration = async (didomiOrganizationId) => {
    if (migrationRecord && props.migrateVendorList) {
      try {
        const migrationInfo = await props.migrateVendorList(migrationRecord.id, didomiOrganizationId);
        if(migrationInfo?.didomiRegulationConfigId) {
          setMigrationInfo(migrationInfo);
        }
      } catch(err) {
        setMigrationError(err);
      } finally {

      }
    }
  };

  const handleCancelMigration = () => {
    setShowMigrationModal(false);
    setMigrationRecord(null);
    setMigrationInfo(null);
    setMigrationError(null);
  };

  useEffect(() => {
    if(vendorListError){
      const serverErrors = handleServerSideErrors(vendorListError, indexSiteNameMap);
      if(serverErrors){
        setShowActivateErrorModal(true)
        setServerErrors(serverErrors)
      } else {
        message.error("Something went wrong", 2)
      }
    }
    return ()=>{
      props.clearVendorListError();
    }
  },[vendorListError])

  const headingMessage = <>Are you sure you want to delete the following {props.entity} <b>{currentRegulation?.name}</b>&nbsp;?</>;
  const errorNote = <></>;
  const okText = "Delete";
  const cancelText = "Cancel";

  const headingDeactivate = (
    <>
      Are you sure you want to{" "}
      {currentRegulation?.isActive ? "deactivate" : "activate"} the following{" "}
      {props.entity} <b>{currentRegulation?.name}</b>&nbsp;?
      {currentRegulation?.isActive ? (
        <>
          <br/><br/>
          <div>By deactivating this {props.entity?.toLowerCase()}, the users' choices made in conjunction with these settings will no longer be respected.</div>
        </>
      ) : null}
    </>
  );
  const okTextDeactivate = currentRegulation?.isActive
    ? "Deactivate"
    : "Activate";

  const errorContent = (
    <React.Fragment>
      <div className="error-wrapper">
        <div className="message-block">
          {headingMessage}
          <div className="note">{errorNote}</div>
        </div>
      </div>
    </React.Fragment>
  );

  const errorContentDeactivate = (
    <React.Fragment>
      <div className="error-wrapper">
        <div className="message-block">
          {headingDeactivate}
          <div className="note">{errorNote}</div>
        </div>
      </div>
    </React.Fragment>
  );

  const errorContentRegulation = renderCannotUpdateRegulationError(currentRegulation, serverErrors, props.entity);

  const cloneVl = (newVlName) => {
    props.cloneVendorList(currentUser.accountId, currentRegulation?.id, newVlName).then((resp) => {
      if(resp){
        message.success(<>{props.entity} <b>{currentRegulation?.name}</b> has been successfully cloned</>)
      }
    })
    setCloneVlModalOpen(false)
  }

  const handleDelete = () => {
    props.deleteVendorList(currentUser?.accountId, currentRegulation?.id).then((resp) => {
    if (resp) {
      message.success(<>{props.entity} <b>{currentRegulation?.name}</b> was successfully deleted</>)
    }
    setDeleteModalOpen(false)
    });
  }


  const togglePropertiesDetail = (record) => {
    setCurrentRegulation(record)
    setShowPropertiesDetail(!showPropertiesDetail)
  }


  const handleDeactivate = () => { 
    if(currentRegulation?.siteIds.length < 1 && !currentRegulation?.isActive) {
      setShowActivateErrorModal(true);
    } else {
      props.activateDeactivateVendorList(currentRegulation).then((resp) => {
        if(resp){
          message.success(<>{props.entity} <b>{currentRegulation?.name}</b> is successfully {currentRegulation?.isActive?'deactivated':'activated'}</>)
        }
      })
    }
    setShowDeactivateModal(false) 
  }

  const handlePropertyFilter = (value) => {
    setSelectedFilterValues(value);
  }

  const isAnyVlStillV2_1 = vendorLists.some(vl => vl.tcfVersion === 2.1);

  const columns = (() => {
    let columnsBase = [
      {
        title: props.entity,
        dataIndex: 'name',
        key: 'name',
        width: '20%',
        align: 'left',
        className: 'name-column',
        shouldCellUpdate: (record, prevRecord) => !shallowEqual(record, prevRecord),
        sorter: (a, b) => a.name.localeCompare(b.name),
        sortOrder: tableStates.sorter.columnKey === 'name' ? tableStates.sorter.order : null,
        filteredValue: searchText.trim().length ? [searchText.trim()] : null,
        onFilter: (value, record) => {
          return (record.name || '').toLowerCase().includes(value.toLowerCase())
        },
        render: (name, record) => (
          <Typography.Paragraph className="pointer" ellipsis={{rows: 2, tooltip: {title: name}}}>{name}</Typography.Paragraph>
        ),
        onCell: (record) => ({ onClick: (e) => {e.stopPropagation();props.handleEdit(record.id)} }),
      },
      {
        title: 'Last Modified',
        dataIndex: 'dateUpdated',
        key: 'dateUpdated',
        align: 'left',
        // width: '100px',
        className: 'last-modified-colum',
        defaultSortOrder: 'descend',
        sorter: (a, b) =>
          !a.dateUpdated && !b.dateUpdated
            ? 0
            : !a.dateUpdated
            ? -1
            : !b.dateUpdated
            ? 1
            : moment(a.dateUpdated).diff(b.dateUpdated),
        sortOrder: tableStates.sorter.columnKey === 'dateUpdated' ? tableStates.sorter.order : null,
        filteredValue: tableStates.filters.dateUpdated || null,
        render: (dateUpdated) => dateUpdated ? dayjs(dateUpdated).format('YYYY-MM-DD hh:mm A') : '',
        shouldCellUpdate: (record, prevRecord) => !shallowEqual(record, prevRecord),
      },
      {
        title: 'Properties',
        dataIndex: 'siteIds',
        key: 'siteIds',
        // width: '100px',
        align: 'right',
        filters: filterPropertyObjects,
        filterSearch: true,
        shouldCellUpdate: (record, prevRecord) => !shallowEqual(record, prevRecord),
        onFilter: (filteredSiteId, record) =>
          filteredSiteId === 'no_property'
            ? record.siteIds.includes(filteredSiteId) || !record.siteIds.length
            : record.siteIds.includes(filteredSiteId),
        sorter: (a, b) => a.siteIds.length - b.siteIds.length,
        sortOrder: tableStates.sorter.columnKey === 'siteIds' ? tableStates.sorter.order : null,
        filteredValue: tableStates.filters.siteIds || null,
        render: (_, record) => (
          <Tooltip title={'Click to view all properties'}>
            <Tag color='processing' className='pointer' bordered onClick={() => togglePropertiesDetail(record)}>{record?.siteIds.length}</Tag>
          </Tooltip>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'isActive',
        key: 'isActive',
        // width: '100px',
        align: 'left',
        className: 'status-column',
        shouldCellUpdate: (record, prevRecord) => !shallowEqual(record, prevRecord),
        filters: [
          { text: 'Active', value: true },
          { text: 'Inactive', value: false },
        ],
        filteredValue: tableStates.filters.isActive || null,
        render: (_, record) =>
          !readOnly && !props.calculateAccess(sitesIds, record.siteIds) ? (
            <Space size='middle'>
              <Switch
                onClick={(e) => toggleDeactivateMessage(record)}
                checked={record.isActive}
                size='small'
              />
              <span>{record.isActive ? 'Active' : 'Inactive'}</span>
            </Space>
          ) : (
            <>
              <i
                className='fas fa-circle'
                style={{ color: record.isActive ? '#52C41A' : '#D9D9D9', width: 10, height: 10, marginRight: 5 }}
              />
              <span>{record.isActive ? 'Active' : 'Inactive'}</span>
            </>
          ),
        onFilter: (value, record) => record.isActive === value,
      },
      {
        title: 'Actions',
        key: 'action',
        // width: '100px',
        align: 'center',
        shouldCellUpdate: (record, prevRecord) => !shallowEqual(record, prevRecord),
        render: (_, record) => {
          const viewOnlyAccess = props.calculateAccess(sitesIds, record.siteIds);
          if (readOnly || viewOnlyAccess) return null;

          return (
            <div style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Clone">
                <Button type="text" icon={<CopyOutlined />} onClick={(e) => handleCopy(e, record)} />
              </Tooltip>
              <Tooltip title="Delete">
                <Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => showDeleteModal(e, record)} />
              </Tooltip>
              {showMigrateAction && userAccess && (
                <Tooltip title="Migrate to Didomi">
                  <span
                    className="clickable"
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: '0 8px', verticalAlign: 'middle' }}
                    onClick={(e) => { e.stopPropagation(); handleMigrationClick(record); }}
                  >
                    <DidomiFavIcon className="clickable" size={16} />
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
    ];

    if (props.gdpr && !props.nonIab) {
      const tcfVersionColumn = {
        title: 'TCF Version',
        dataIndex: 'tcfVersion',
        key: 'tcfVersion',
        // width: '100px',
        align: 'right',
        className: 'tcfV-column',
        shouldCellUpdate: (record, prevRecord) => !shallowEqual(record, prevRecord),
        ...(isAnyVlStillV2_1 && {
          sorter: (a, b) => parseFloat(a.tcfVersion) - parseFloat(b.tcfVersion),
        }),
        sortOrder: tableStates.sorter.columnKey === 'tcfVersion' ? tableStates.sorter.order : null,
        filteredValue: tableStates.filters.tcfVersion || null,
        ...(isAnyVlStillV2_1 && {
          filters: [
            { text: '2.1', value: 2.1 },
            { text: '2.3', value: 2.3 },
          ],
        }),
        render: (_, record) => record.tcfVersion == 2.1 ? (
          <Infotip
            className='usp-infotip count-container pointer'
            trigger='hover'
            title={null}
            content={'Update to TCF v2.3 pending. Please review the updates to transition to the latest version.'}
          >
            <div>
              {record.tcfVersion}
              {record.tcfVersion == 2.1 ? <i className='fas fa-circle' style={{ color: '#FCA015' }} /> : null}
            </div>
          </Infotip>
        ) : (
          <div>
            {record.tcfVersion}
            {record.tcfVersion == 2.1 ? <i className='fas fa-circle' style={{ color: '#FCA015' }} /> : null}
          </div>
        ),
        onFilter: (value, record) => record.tcfVersion === value,
      };
      columnsBase.splice(2, 0, tcfVersionColumn);
    }

    if (showMigrationColumns) {
      columnsBase = columnsBase.concat(migrationColumn);
    }

    if (props.usnat) {
      const regulationType = {
        // Allow the header to wrap between the two words, but never mid-word
        // (antd's default `word-break: break-word` splits "Regulation").
        title: <span style={{ wordBreak: 'keep-all', overflowWrap: 'normal' }}>Regulation Type</span>,
        dataIndex: 'legislation',
        key: 'legislation',
        // width: '150px',
        align: 'left',
        sorter: (a, b) => {
          const regTypeForA = a?.nonIab ? 'Standard' : a.legislation;
          const regTypeForB = b?.nonIab ? 'Standard' : b.legislation;
          return regTypeForA?.localeCompare(regTypeForB);
        },
        sortOrder: tableStates.sorter.columnKey === 'legislation' ? tableStates.sorter.order : null,
        filteredValue: tableStates.filters.legislation || null,
        render: (legislation, record) => {
          if (record?.nonIab) return <Typography.Text>Standard</Typography.Text>;
          // 'hybrid' is the Global Privacy Protocol flow — the label is too wide for
          // this column now, so the IAB tag stands in for it with a tooltip.
          const regLabel = legislation === 'usnat' ? 'National' : null;
          const iabTag = (
            <Tooltip title={legislation === 'hybrid' ? 'Global Privacy Protocol' : 'IAB'}>
              <Tag color='orange'>IAB</Tag>
            </Tooltip>
          );
          return (
            <Typography.Text>
              <Space size={8}>{regLabel}{iabTag}</Space>
            </Typography.Text>
          );
        },
      };
      columnsBase.splice(1, 0, regulationType);

      if (currentUser?.accountFeatures?.includes('usnat_v2')) {
        const usNatVersionColumn = {
          title: 'USNat Version',
          dataIndex: 'USNatVersion',
          key: 'USNatVersion',
          // width: '15%',
          render: (USNatVersion, record) =>
            record?.nonIab ? (
              <Tag>Not Applicable</Tag>
            ) : (
              <Tag color={USNatVersion === 2 ? 'green' : 'blue'}>
                v{USNatVersion || 1}
              </Tag>
            ),
        };
        columnsBase.splice(2, 0, usNatVersionColumn);
      }
    }

    return columnsBase;
  })();

  const getVlNameError = (oldVlName, newVlName) => {
    let nameError = null;
    const duplicateName = vendorLists.map(al => al.name).find(vl => vl === newVlName.trim());
    if(newVlName.trim().length === 0){
      nameError = <>Name field can not be empty</>
    }else if(duplicateName && duplicateName !== oldVlName) {
      nameError = <>{props.entity} with name '<strong>{newVlName}</strong>' already exists</>
    }
    return nameError; 
  }

  let content;
  if (!currentUser || !sites) {
    content = <Loading />;
  } else if(vendorLists.size) {
    content = (
      <Table
        className="us-privacy-table"
        style={{marginTop:'20px'}}
        columns={columns}
        dataSource={vendorListDataSource}
        bordered={false}
        onChange={(pagination, filters, sorter) => {
          setTableStates({sorter,filters,pagination});
          setSelectedFilterValues(filters.siteIds);
        }}
        pagination={false}
        scroll={{
          y: `calc(100vh - 350px)`,
        }}
        loading={vendorListsPending || isSearchLoading}
        tableLayout="fixed"
      />
    )
  } else if(vendorListsPending){
    content = <Loading />;
  } else if(!readOnly){
    content = (
      <div className='no-lists-message-container'>
        <p className='title'>
          You haven’t created a Vendor List yet.
        </p>
        <p className='descr'>
          We use vendor lists to make updating easy. Any changes you make, update automatically across all Privacy Managers using that list.
        </p>
        <Button type="primary" onClick={props.handleCreate}>Get Started</Button>
      </div>
    );
  } else {
    content = (
      <div className='no-lists-message-container'>
        <p className='title'>
          There are no Vendor Lists available.
        </p>
      </div>
    );
  }

  let title = null;
  let subTitle = null;
  let viewOnlyModeAlert = [];
  if(props.usnat){
    title = "U.S. Multi-State Privacy"
    subTitle = <h5>By implementing these settings, websites can help ensure that they are compliant with US Privacy regulations and protect user data appropriately.</h5>
    viewOnlyModeAlert = props.readOnly ? [<><b>View Only:</b> User does not has access to U.S. Multi State Regulations</>] : []
  } else if(props.gdpr) {
    title = (props.nonIab ? "GDPR Standard - " : "GDPR TCF - ") + "Vendor Lists"
    subTitle = <h5>{!props.nonIab ? "These settings enable you to select and curate lists of vendors that are processing user data on your properties under GDPR. They also allow you to customise settings that meet the requirements of the IAB's Transparency and Consent Framework." : null}</h5>
    viewOnlyModeAlert = props.readOnly ? [ <> <b>View Only:</b> User does not has access to GDPR Vendor Lists</>] : [];
    if(!props.isAccountPartOfWave1){
      viewOnlyModeAlert.splice(0,0, "As Sourcepoint migrates clients from TCF V2.1 to TCFV2.3 we are taking extra precautions to ensure that this goes smoothly. At present your account is not enabled for TCFV2.3 transition and as such you will not be able to perform actions on your TCF vendor lists at this time (including edit, duplicate, activate, or deactivate). Please reach out to your account manager for details of when your account will be activated and if you have any questions." )
    };
  } else if(props.globalStandard) {
    title = "Global Enterprise"
    viewOnlyModeAlert = props.readOnly ? [<> <b>View Only:</b> User does not has access to Global Enterprise</>] : []
  }

    const downloadActiveEntities = () => {
      const items = vendorLists.toJS();

      if (!items.length) {
        message.info(`No ${props.entity}s available to download.`);
        return;
      }

      const rows = items.map((item) => ({
        [`${props.entity} Name`]: item.name || "",
        [`${props.entity} ID`]: item.id || "",
        status: item.isActive ? "Active" : "Inactive",
        ...(props.gdpr && !props.nonIab
          ? { tcfVersion: item.tcfVersion || "" }
          : {}),
        "last modified": item.dateUpdated
          ? dayjs(item.dateUpdated).format("MMMM D, YYYY, h:mm A")
          : "",
        "last migrated": item.lastMigratedDate
          ? dayjs(item.lastMigratedDate).format("MMMM D, YYYY, h:mm A")
          : "",
        "migration status": item.migrationStatus || "",
      }));
      debugger;
      const csvContent = convertToCSV(rows);
      downloadCsv(
        csvContent,
        `${title} ${!props.gdpr ? `${props.entity.replace(/\s+/g, "-").toLowerCase()}s` : ''}.csv`,
      );
    };


  return (
        <>
        <div className="us-privacy-list-layout">
          {readOnly ? viewOnlyModeAlert.map( m => <Alert style={{marginBottom: '10px'}} type="error" message={m} showIcon />) : null}
          <h2>{title}</h2>
          {subTitle}
          <div className="us-privacy-search-add">
            <Input.Search
              placeholder={"Search for " + props.entity + " names"}
              type="search"
              onChange={({ target: { value } }) => {
                setIsSearchLoading(true);
                debouncedChangeHandler(value);
              }}
              style={{width:360}}
            />
            <Space>
              {showDownloadActive && (
                <Button disabled={vendorListsPending || isSearchLoading} className="download-active" onClick={downloadActiveEntities} type="default" style={{ marginRight: token.margin }}>
                  Download {props.entity}s
                </Button>
              )}
              {!readOnly  ? <Button className="add-new" onClick={props.handleCreate} type="primary">+ New {props.entity}</Button> : null}
            </Space>
          </div>
          {content}
        </div>
      {cloneVlModalOpen && (
        <CloneModal
          type="vendor-management"
          cloneMethod={cloneVl}
          name={currentRegulation?.name}
          closeModal={() => setCloneVlModalOpen(false)}
          getNameError={getVlNameError}
          entity={props.entity}
          entityType={"Inactive"}
        />
      )}
      {deleteModalOpen && (<DismissErrorModal
        modalWrapperClass="operation-confirmation"
        title={`Delete ${props.entity}`}
        error={errorContent}
        isModalVisible={deleteModalOpen}
        handleCancel={()=>setDeleteModalOpen(false)}
        renderOk
        okText={okText}
        cancelText={cancelText}
        handleOk={handleDelete}
        primaryAction="submit"
      />)}
      {showDeactivateModal && (<DismissErrorModal
        modalWrapperClass="operation-confirmation"
        title= {`${currentRegulation?.isActive? "Deactivate":"Activate"} ${props.entity}`}
        error={errorContentDeactivate}
        isModalVisible={showDeactivateModal}
        handleCancel={() => toggleDeactivateMessage(null)}
        renderOk
        okText={okTextDeactivate}
        cancelText={cancelText}
        handleOk={handleDeactivate}
        primaryAction="submit"
      />)}
      {showActivateErrorModal && (<DismissErrorModal
        modalWrapperClass="activate-confirmation"
        title={`Activate ${props.entity}`}
        error={errorContentRegulation}
        isModalVisible={showActivateErrorModal}
        handleCancel={() => setShowActivateErrorModal(false)}
        renderOk={false}
        okText={"OK"}
        cancelText={"Close"}
        handleOk={() => setShowActivateErrorModal(false)}
        primaryAction="submit"
      />)}
      {showPropertiesDetail && (
        <PropertiesDetailModal
          siteIds={currentRegulation?.siteIds}
          sites={sites}
          siteGroups={siteGroups}
          showPropertiesDetailModal={showPropertiesDetail}
          setShowPropertiesDetailsModal={setShowPropertiesDetail}
          setShowActivateErrorModal={setShowPropertiesDetail}
          indexSiteMap={indexSiteNameMap}
          filteredSiteIds={selectedFilterValues}
        />
      )}
      {showMigrationModal && (
        <DidomiMigrationModal
          visible={showMigrationModal}
          onConfirm={handleConfirmMigration}
          onCancel={handleCancelMigration}
          didomiOrganizations={props.didomiOrganizations}
          entity={props.entity}
          loading={vendorListsPending}
          migrationInfo={migrationInfo}
          migrationError={migrationError}
          setMigrationError={setMigrationError}
          migrationRecord={migrationRecord}
        />
      )}
      </>
  )
}

export default RegulationListGeneric;
